import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Typography } from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  CloudOffOutlined as CloudOffIcon,
  DeviceThermostat as ThermostatIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import type { Pour } from '../../services/pours';
import { buildPourRow } from './pourCardModel';
import type { PourFlagIcon, PourFlagTone, PourRowTarget, PourRowTile } from './pourCardModel';

const FLAG_ICONS: Record<PourFlagIcon, React.ReactElement> = {
  overLimit: <ThermostatIcon sx={{ fontSize: 15 }} />,
  offline: <CloudOffIcon sx={{ fontSize: 15 }} />,
  sensor: <WarningAmberIcon sx={{ fontSize: 15 }} />,
};

/** Text shades that stay readable in sunlight, and their tint and edge. */
export const FLAG_COLORS: Record<PourFlagTone, { text: string; tint: string; edge: string }> = {
  error: { text: '#b3261e', tint: '#fdecea', edge: '#b3261e' },
  warning: { text: '#8a5300', tint: '#fff4e0', edge: '#d88c00' },
};

export interface MobilePourRowProps {
  pour: Pour;
  /** The live clock (useNow). */
  now: number;
  /** The last successful list fetch: whether a pour has gone quiet is
   *  judged against it, not the wall clock. */
  freshnessNow?: number;
  /** The row was tapped. */
  onOpen: (pour: Pour) => void;
  /** Draw the row muted (the Awaiting review group). */
  muted?: boolean;
}

/** The labelled readings: two or three tiles a row, four as two by two, and
 *  a single tile at half width. */
export const ReadingTiles: React.FC<{ tiles: PourRowTile[]; muted?: boolean }> = ({ tiles, muted = false }) => {
  if (tiles.length === 0) return null;
  const columns = tiles.length === 4 ? 2 : Math.min(Math.max(tiles.length, 2), 3);
  return (
    <Box
      data-testid="reading-tiles"
      sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 1, mt: 1.25 }}
    >
      {tiles.map((tile) => (
        <Box
          key={tile.key}
          data-testid={`tile-${tile.key}`}
          sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 1, py: 0.75, minWidth: 0 }}
        >
          <Typography
            component="div"
            noWrap
            sx={{
              fontSize: '0.65625rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {tile.label}
          </Typography>
          <Typography
            component="div"
            noWrap
            sx={{
              fontSize: '1rem',
              fontWeight: 650,
              lineHeight: 1.35,
              color: tile.bad ? FLAG_COLORS.error.text : muted ? 'text.secondary' : 'text.primary',
            }}
          >
            {tile.value}{' '}
            <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>
              {tile.unit}
            </Box>
          </Typography>
          {tile.sub && (
            <Typography component="div" noWrap sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
              {tile.sub}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

/** The bar to the next target, with "Next: Stripping 20 MPa" and "1 of 3
 *  reached" under it. */
export const TargetBar: React.FC<{ target: PourRowTarget | null }> = ({ target }) => {
  const { t } = useTranslation();
  if (!target) return null;
  const done = target.next === null && target.reached >= target.total;
  const percent = Math.round((done ? 1 : target.fraction) * 100);
  return (
    <Box data-testid="target-bar" sx={{ mt: 1.25 }}>
      <Box
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}
      >
        <Box sx={{ width: `${Math.max(percent, 3)}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 3 }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5, fontSize: '0.71875rem', color: 'text.secondary' }}>
        {done ? (
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t('mobile.controlPanel.target.allReached', {
              count: target.total,
              defaultValue_one: 'Target reached',
              defaultValue_other: 'All {{count}} targets reached',
            })}
          </Box>
        ) : (
          <>
            <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t('mobile.controlPanel.target.next', { defaultValue: 'Next:' })}{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {target.next}
              </Box>
            </Box>
            <Box component="span" sx={{ flexShrink: 0 }}>
              {t('mobile.controlPanel.target.reached', {
                defaultValue: '{{reached}} of {{total}} reached',
                reached: target.reached,
                total: target.total,
              })}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

/** The problem chip on the right of the name: icon and words, never colour
 *  alone. */
export const FlagChip: React.FC<{ tone: PourFlagTone; icon: PourFlagIcon; text: string; large?: boolean }> = ({
  tone,
  icon,
  text,
  large = false,
}) => (
  <Box
    component="span"
    data-testid="row-flag"
    sx={{
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: large ? 1.25 : 1,
      py: large ? '3px' : 0.25,
      borderRadius: 2.5,
      fontSize: large ? '0.78125rem' : '0.75rem',
      fontWeight: 650,
      whiteSpace: 'nowrap',
      bgcolor: FLAG_COLORS[tone].tint,
      color: FLAG_COLORS[tone].text,
    }}
  >
    {FLAG_ICONS[icon]}
    {text}
  </Box>
);

/** The soft card's shadow, also used by the placeholder rows. */
export const SOFT_CARD_SHADOW = '0 1px 2px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.05)';

/**
 * One pour on the phone Control Panel as a soft card on the grey page: the
 * Pour Description with the Pour ID tag on its right, then "Project, start
 * date" with the problem chip (only when there is one), and a chevron. A
 * problem also colours the card's left edge. The whole card opens the
 * pour's dashboard, where the readings, Stop Pour and the report live.
 */
const MobilePourRow: React.FC<MobilePourRowProps> = ({ pour, now, freshnessNow, onOpen, muted = false }) => {
  const { t } = useTranslation();
  const row = useMemo(() => buildPourRow(pour, now, t, { freshnessNow }), [pour, now, freshnessNow, t]);

  return (
    <ButtonBase
      onClick={() => onOpen(pour)}
      aria-label={row.ariaLabel}
      data-testid="pour-row"
      data-section={row.section}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'left',
        bgcolor: 'background.paper',
        borderLeft: row.flag ? `4px solid ${FLAG_COLORS[row.flag.tone].edge}` : undefined,
        borderRadius: '18px',
        boxShadow: SOFT_CARD_SHADOW,
        pl: row.flag ? '14px' : '18px',
        pr: 1.75,
        py: '17px',
        mb: 1.5,
        transition: 'box-shadow 0.15s',
        '&:hover, &.Mui-focusVisible': { boxShadow: '0 2px 8px rgba(16,24,40,.12)' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            component="span"
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '1.03125rem',
              fontWeight: 600,
              lineHeight: 1.3,
              color: muted ? 'text.secondary' : 'text.primary',
            }}
          >
            {row.title}
          </Typography>
          {row.code && (
            <Box
              component="span"
              data-testid="row-code"
              sx={{
                flexShrink: 0,
                // Compact: real IDs ("HM-2610-0001") are long, and the name
                // needs the room.
                px: '6px',
                py: '1px',
                borderRadius: '5px',
                bgcolor: '#f0f2f4',
                color: '#5a6067',
                fontSize: '0.6875rem',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {row.code}
            </Box>
          )}
        </Box>
        {(row.projectLine || row.flag || row.scheduled) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '6px' }}>
            <Typography
              component="span"
              noWrap
              data-testid="row-line2"
              sx={{ flex: 1, minWidth: 0, fontSize: '0.84375rem', color: 'text.secondary', lineHeight: 1.4 }}
            >
              {row.projectLine}
            </Typography>
            {row.flag && <FlagChip tone={row.flag.tone} icon={row.flag.icon} text={row.flag.text} large />}
            {row.scheduled && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  px: 1.25,
                  py: '3px',
                  borderRadius: 2.5,
                  fontSize: '0.78125rem',
                  fontWeight: 650,
                  bgcolor: '#eef2f7',
                  color: '#38506e',
                }}
              >
                {t('mobile.controlPanel.row.scheduled', { defaultValue: 'Scheduled' })}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <ChevronRightIcon aria-hidden sx={{ flexShrink: 0, fontSize: 20, color: '#b5bac0' }} />
    </ButtonBase>
  );
};

// Memoised: the page re-renders on every header and busy change, and a row
// only needs to when its pour, the clock or the fetch time changes.
export default React.memo(MobilePourRow);
